
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { referralSetupSchema, ReferralSetupFormValues } from '@/components/onboarding/ReferralSetupForm';
import { createInstanceFromOnboarding } from '@/utils/instanceService';
import { SignupData } from '@/types/onboarding.d';

export const useBusinessOnboarding = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [setupComplete, setSetupComplete] = useState(false);
  const [signupData, setSignupData] = useState<SignupData | null>(null);
  const [onboardingData, setOnboardingData] = useState<{
    companyName: string;
    subdomain: string;
  } | null>(null);

  // Initialize form for referral program setup
  const referralForm = useForm<ReferralSetupFormValues>({
    resolver: zodResolver(referralSetupSchema),
    defaultValues: {
      initialReferralUrl: 'https://',
      supportEmail: '',
    },
  });

  // Retrieve signup data from session storage on component mount
  useEffect(() => {
    const storedSignupData = sessionStorage.getItem('signupData');
    
    if (!storedSignupData) {
      toast({
        title: "Missing account information",
        description: "Please complete the previous step first.",
        variant: "destructive",
      });
      navigate('/business-signup');
      return;
    }
    
    try {
      const parsedData = JSON.parse(storedSignupData);
      setSignupData(parsedData);
      
      // Pre-fill support email with the user's email if available
      if (parsedData.email) {
        referralForm.setValue('supportEmail', parsedData.email);
      }
    } catch (error) {
      console.error("Error parsing signup data:", error);
      toast({
        title: "Error loading data",
        description: "There was a problem loading your account information.",
        variant: "destructive",
      });
      navigate('/business-signup');
    }
  }, [navigate, toast, referralForm]);

  const handleBusinessSetup = (data: ReferralSetupFormValues) => {
    setIsLoading(true);
    
    if (!signupData) {
      toast({
        title: "Missing account information",
        description: "Please complete the previous step first.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    // In a real app, this would collect business details
    const onboardingFormData = {
      companyName: signupData.name + "'s Business",
      subdomain: signupData.name.toLowerCase().replace(/[^a-z0-9]/g, ''),
      initialReferralUrl: data.initialReferralUrl,
      supportEmail: data.supportEmail,
      primaryColor: "#4F46E5", // Add the missing primaryColor field with a default value
      paymentFrequency: 'monthly' as 'monthly' | 'quarterly' | 'yearly', // Add the required paymentFrequency field
      resellerSubdomain: 'main' // Add a default resellerSubdomain
    };
    
    // Set this for displaying in the success view
    setOnboardingData({
      companyName: onboardingFormData.companyName,
      subdomain: onboardingFormData.subdomain
    });
    
    // Simulate API call to set up the referral platform
    createInstanceFromOnboarding(signupData, onboardingFormData)
      .then((response) => {
        if (response.success) {
          setCurrentStep(2);
          setTimeout(() => setSetupComplete(true), 1500);
        } else {
          toast({
            title: "Setup failed",
            description: response.message || "There was a problem setting up your business.",
            variant: "destructive",
          });
        }
      })
      .catch((error) => {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleBackStep = () => {
    navigate('/business-account-create');
  };

  const handleContinueToDashboard = () => {
    // Clear session storage data since onboarding is complete
    sessionStorage.removeItem('signupData');
    
    // In a real app, this would navigate to the user's dashboard
    toast({
      title: "Setup complete!",
      description: "Your business account has been successfully created.",
    });
    navigate('/');
  };

  return {
    isLoading,
    currentStep,
    setupComplete,
    signupData,
    onboardingData,
    referralForm,
    handleBusinessSetup,
    handleBackStep,
    handleContinueToDashboard
  };
};
